import React, { useCallback, useEffect, useId, useState } from 'react';
import Button3D from '../ui/Button3D';
import { supabaseClient } from '../../config/supabase';

export function TripChangeReviewItem({ request, client, canEdit, onResolved }) {
    const id = useId();
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    async function review(decision) {
        setBusy(true); setError('');
        try {
            const { error: failure } = await client.rpc('m9_staff_review_change', { p_request: request.id, p_decision: decision, p_note: note });
            if (failure) throw failure;
            await onResolved();
        } catch (failure) { setError(failure.message || 'The request could not be reviewed.'); }
        finally { setBusy(false); }
    }
    return <article className="rounded-xl border border-[var(--surface-border)] bg-[var(--surface-raised)] p-4 space-y-3">
        <h3 className="font-semibold">{request.kind === 'cancel' ? 'Cancellation requested' : 'Reschedule requested'} · {request.confirmation_number}</h3>
        {request.proposed_start && <p>Requested pickup: {new Date(request.proposed_start).toLocaleString()}</p>}
        {request.reason && <p>Reason: {request.reason}</p>}
        {request.note && <p className="whitespace-pre-wrap break-words">Client note: {request.note}</p>}
        <p>Status: {request.status}</p>
        {request.staff_note && <p className="whitespace-pre-wrap break-words">Staff note: {request.staff_note}</p>}
        {error && <p role="alert" className="text-[var(--ui-negative)]">{error}</p>}
        {canEdit && request.status === 'pending' && <>
            <label className="block" htmlFor={`${id}-note`}>Note to the client (optional)</label>
            <textarea id={`${id}-note`} maxLength={2000} value={note} disabled={busy} onChange={(event) => setNote(event.target.value)}
                className="w-full min-h-[88px] rounded-lg border border-[var(--surface-border)] bg-[var(--surface-raised)] p-3" />
            <div className="flex flex-wrap gap-3">
                <Button3D disabled={busy} className="min-h-[44px]" style={{ minHeight: 44 }} onClick={() => review('approved')}>Approve</Button3D>
                <Button3D disabled={busy} className="min-h-[44px]" style={{ minHeight: 44 }} onClick={() => review('declined')}>Decline</Button3D>
            </div>
        </>}
    </article>;
}

export default function TripChangeReview({ client = supabaseClient, engagementId = null, canEdit = false, onChanged }) {
    const [requests, setRequests] = useState([]);
    const [error, setError] = useState('');
    const load = useCallback(async () => {
        try {
            const { data, error: failure } = await client.rpc('m9_staff_trip_changes', { p_engagement: engagementId });
            if (failure || !Array.isArray(data)) throw new Error('Client trip change requests could not be loaded.');
            setRequests(data); setError('');
        } catch (failure) { setRequests([]); setError(failure.message); }
    }, [client, engagementId]);
    useEffect(() => { load(); const timer = setInterval(load, 15000); return () => clearInterval(timer); }, [load]);
    async function resolved() { await load(); await onChanged?.(); }
    if (!error && !requests.length) return null;
    return <section className="space-y-3 text-[var(--ink)]" aria-label="Client trip change requests">
        <h2 className="text-lg font-semibold">Client trip change requests</h2>
        {error && <p role="alert">{error}</p>}
        {requests.map((request) => <TripChangeReviewItem key={request.id} request={request} client={client} canEdit={canEdit} onResolved={resolved} />)}
    </section>;
}
